import { Fire, Snowflake } from "@phosphor-icons/react";
import { Badge } from "@replicate/ui";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import type { ComponentProps } from "react";
import { queryClient } from "../query";
import type { Model } from "../types";
import { route } from "../urls";

export default function ModelStatusIndicator({
  initialStatus,
  model,
}: {
  initialStatus: "offline" | "online";
  model: Model;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <ModelStatusIndicatorInner initialStatus={initialStatus} model={model} />
    </QueryClientProvider>
  );
}

function ModelStatusIndicatorInner({
  initialStatus,
  model,
}: ComponentProps<typeof ModelStatusIndicator>) {
  const query = useQuery({
    initialData: {
      status: initialStatus,
    },
    retry: false,
    queryKey: ["model-status", model.owner, model.name],
    refetchOnWindowFocus: true,
    refetchInterval: 15 * 1000,
    queryFn: async () => {
      const res = await fetch(
        route("model_status", {
          username: model.owner,
          name: model.name,
        }),
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRFToken": Cookies.get("csrftoken") ?? "",
          },
        }
      );

      if (!res.ok) {
        throw new Error("Failed to fetch model status");
      }

      return res.json<{ status: "online" | "offline" | null }>();
    },
  });

  if (query.isError) return null;
  if (query.isSuccess && query.data.status === null) return null;

  return (
    <a
      className="no-underline no-focus no-default"
      href={`${route("docs_how_does_replicate_work")}#cold-boots`}
    >
      <Badge
        startIcon={
          query.data.status === "offline" ? (
            <Snowflake />
          ) : (
            <Fire weight="fill" />
          )
        }
        variant={query.data.status === "offline" ? "accent" : "success"}
        rounded
      >
        {query.data.status === "offline" ? "Cold" : "Warm"}
      </Badge>
    </a>
  );
}
